import {
  combineReducers,
  configureStore,
  isRejectedWithValue,
  Middleware,
  Reducer,
  UnknownAction
} from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist';

import { apiServices } from 'services';
import { marketoneApi } from 'services/marketone/marketone';

import { notifyError } from 'shared/Toast/Toast';

import { IS_LIVE } from 'utils/constants';
import { getBrowserLocalStorage } from 'utils/storage';

import {
  createClearUserPersistAfterMiddleware,
  PERSIST_ROOT_KEY
} from './middleware/clearUserPersistAfterMiddleware';
import {
  authPersistTransform,
  syncAuthTokensToSessionMiddleware
} from './persistence/authSessionPersistence';
import { sliceReducers } from './slices';

interface Payload {
  data?: {
    message?: string;
  };
  status: number | string;
}

type PersistStorage = {
  getItem: (key: string) => Promise<string | null>;
  removeItem: (key: string) => Promise<void>;
  setItem: (key: string, value: string) => Promise<string>;
};

const createNoopStorage = (): PersistStorage => ({
  getItem: async () => null,
  removeItem: async () => undefined,
  setItem: async (_key: string, value: string) => value
});

const createWebStorage = (): PersistStorage => ({
  getItem: async (key: string) => {
    try {
      return getBrowserLocalStorage()?.getItem(key) ?? null;
    } catch {
      return null;
    }
  },
  removeItem: async (key: string) => {
    try {
      getBrowserLocalStorage()?.removeItem(key);
    } catch {
      // storage unavailable (SecurityError, private mode, blocked third-party)
    }
  },
  setItem: async (key: string, value: string) => {
    try {
      getBrowserLocalStorage()?.setItem(key, value);
    } catch {
      // storage unavailable — continue without persisting (same as noop storage)
    }
    return value;
  }
});

const storage = typeof window !== 'undefined' ? createWebStorage() : createNoopStorage();

/**
 * API slices whose failures must not raise the global error toast. Their data feeds surfaces that
 * are absent for the vast majority of patients — the MarketONE entitlements lookup runs on the
 * billing page and in the appointment flow for everyone, but only AT&T patients have an AT&T
 * section to show — so a 500 there would put a server error in front of people with nothing at
 * stake. The action still rejects, so consumers keep RTK Query's error state and its last
 * successful payload; only the toast is skipped.
 */
const SILENT_ERROR_REDUCER_PATHS: string[] = [marketoneApi.reducerPath];

const isSilentErrorAction = (type: unknown): boolean =>
  typeof type === 'string' &&
  SILENT_ERROR_REDUCER_PATHS.some((path) => type.startsWith(`${path}/`));

const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  if (isRejectedWithValue(action) && !isSilentErrorAction(action.type)) {
    const payload = action.payload as Payload;

    if (payload && typeof payload === 'object' && 'status' in payload) {
      if (payload.status === 500 && payload.data?.message !== 'Subscription does not exist') {
        notifyError(
          payload.data?.message ||
            'Something is wrong with the server, and we are already working on fixing it.'
        );
        // eslint-disable-next-line no-console
        console.error(payload.data?.message);
      } else if (payload.status === 'FETCH_ERROR') {
        const meta = (action as { meta?: { baseQueryMeta?: { request?: { url?: string } } } }).meta;
        if (meta?.baseQueryMeta?.request?.url?.includes('lifemd')) {
          notifyError('Server is updating at the moment, we need a couple of minutes', false, {
            toastId: 'fetch_error'
          });
        }
        // eslint-disable-next-line no-console
        console.error('CORS ERROR');
      }
    }
  }

  return next(action);
};

const persistConfig = {
  key: 'root',
  storage,
  transforms: [authPersistTransform],
  whitelist: [
    'user',
    'signUp',
    'provider',
    'appointments',
    'mif',
    'renewal',
    'orchestrate',
    'join',
    'programs',
    'programsCatalog'
  ]
};

type ApiReducerMap = {
  [Api in (typeof apiServices)[number] as Api['reducerPath']]: Api['reducer'];
};

const apiReducers = Object.fromEntries(
  apiServices.map((api) => [api.reducerPath, api.reducer])
) as ApiReducerMap;

const apiMiddlewares = apiServices.map((api) => api.middleware);

const appReducer = combineReducers({
  ...sliceReducers,
  ...apiReducers
});

const clearUserPersistAfterMiddleware = createClearUserPersistAfterMiddleware(() =>
  storage.removeItem(PERSIST_ROOT_KEY)
);

const rootReducer: Reducer = (state: RootState, action: UnknownAction) => {
  if (action.type === 'user/clearUser') {
    state = {} as RootState;
  }
  return appReducer(state, action);
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  devTools: !IS_LIVE,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
      }
    }).concat([
      ...apiMiddlewares,
      rtkQueryErrorLogger,
      syncAuthTokensToSessionMiddleware,
      clearUserPersistAfterMiddleware
    ]),
  reducer: persistedReducer
});

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof appReducer>;
export type AppDispatch = typeof store.dispatch;

export { selectAppointments, selectNewAppointment } from './appointments/appointmentsSlice';
export { selectHealthProfile } from './healthProfile/healthProfileSlice';
export { selectJoin } from './join/joinSlice';
export { selectMifInfo } from './mif/mifSlice';
export { selectModalState } from './modals/modalSlice';
export {
  selectGroupedNotifications,
  selectHasMore,
  selectHasUnreadNotifications,
  selectHighPriorityUnreadCount,
  selectIsError,
  selectIsLoading,
  selectIsLoadingMore,
  selectPaginatedCount,
  selectTotalCount,
  selectNotificationsList,
  selectShouldAnimateIndicator
} from './notifications/notificationsSlice';
export { selectOrchestrate } from './orchestrate-signup/orchestrateSlice';
export { selectRenewalInfo } from './renewal/renewalSlice';
export { selectNotificationPreferences } from './settings/settingsSlice';
export { selectShop } from './shop/shopSlice';
export {
  selectSelectedPricePoint,
  selectSignUp,
  selectStatesWhereAshIsAvailable
} from './signup/signupSlice';
export { selectUser, selectWriteMessageFeatureStatus } from './user/userSlice';
export { selectWmPages, selectWmSections } from './wm/wmSlice';
